// src/api/creditoEmpresa.js
import { apiFetch } from './index.js';

/**
 * Obtener resumen de créditos unificado (Compras, Ventas, Reparaciones, Servicios Externos)
 * @returns {Promise} Resumen de créditos
 */
export const obtenerResumenCreditos = async () => {
    try {
        const response = await apiFetch('/CreditoEmpresa/resumen');
        return {
            success: true,
            data: response.data || response,
            message: 'Resumen de créditos obtenido exitosamente'
        };
    } catch (error) {
        console.error('❌ Error obteniendo resumen de créditos:', error);
        return {
            success: false,
            error: error.message,
            data: { compras: [], ventas: [], reparaciones: [], serviciosExternos: [] }
        };
    }
};

/**
 * Registrar un pago de compra
 * @param {Object} pago - Datos del pago
 * @returns {Promise} Resultado del registro
 */
export const registrarPagoCompra = async (pago) => {
    try {
        const response = await apiFetch('/PagoCompra', {
            method: 'POST',
            body: pago
        });
        return {
            success: true,
            data: response.data || response,
            message: 'Pago registrado exitosamente'
        };
    } catch (error) {
        console.error('❌ Error registrando pago de compra:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Registrar un pago de reparación (Salida Equipo)
 * @param {Object} pago - Datos del pago
 * @returns {Promise} Resultado del registro
 */
export const registrarPagoReparacion = async (pago) => {
    try {
        // 1. Registrar el pago a nivel global de la salida
        const response = await apiFetch('/PagoSalidaEquipo', {
            method: 'POST',
            body: pago
        });

        // 2. Si viene de una cuota específica (Cashea o Empresa), registrar el pago de esa cuota
        if (pago.idCuota) {
            try {
                // Obtener detalle actual de la cuota para no exceder saldo pendiente por fluctuación de tasa en Bs
                let cuotaInfo = null;
                try {
                    cuotaInfo = await apiFetch(`/CreditoSalidaEquipo/${pago.idCuota}`);
                } catch (errGet) {
                    console.warn(`⚠️ No se pudo obtener detalle de cuota ${pago.idCuota}:`, errGet);
                }

                const saldoUsdCuota = cuotaInfo?.saldoUsd != null ? Number(cuotaInfo.saldoUsd) : Number(pago.montoUsd);
                const saldoBsCuota = cuotaInfo?.saldoBs != null ? Number(cuotaInfo.saldoBs) : (Number(pago.montoBs) || 0);

                // Solo pagar si aún hay saldo pendiente
                if (saldoUsdCuota > 0 || saldoBsCuota > 0) {
                    const montoPagoUsd = Math.min(Number(pago.montoUsd) || 0, saldoUsdCuota);

                    // Si el monto en USD cancela el saldo total de la cuota, o si el monto en Bs calculado (por la tasa actual)
                    // excede el saldoBs de la cuota (registrado con una tasa anterior), enviamos exactamente saldoBsCuota
                    let montoPagoBs = Number(pago.montoBs) || 0;
                    if (montoPagoUsd >= saldoUsdCuota || montoPagoBs > saldoBsCuota) {
                        montoPagoBs = saldoBsCuota;
                    } else {
                        montoPagoBs = Math.min(montoPagoBs, saldoBsCuota);
                    }

                    await apiFetch('/CreditoSalidaEquipo/pagar', {
                        method: 'POST',
                        body: {
                            idCreditoSalidaEquipo: pago.idCuota,
                            montoPagoUsd: montoPagoUsd,
                            montoPagoBs: montoPagoBs,
                            fechaPago: pago.fechaPago || new Date().toISOString(),
                            observaciones: pago.referencia || "Pago de cuota"
                        }
                    });
                }
            } catch (errCuota) {
                console.warn('⚠️ Advertencia al aplicar pago a la cuota CreditoSalidaEquipo:', errCuota);
                // Si la cuota ya estaba saldada o arrojó "excede el saldo", no arruinar la transacción puesto que PagoSalidaEquipo ya se registró correctamente
                if (!errCuota.message?.toLowerCase().includes('excede el saldo')) {
                    throw errCuota;
                }
            }
        }

        return {
            success: true,
            data: response.data || response,
            message: 'Pago registrado exitosamente'
        };
    } catch (error) {
        console.error('❌ Error registrando pago de reparación:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Registrar un pago de Servicio Externo
 * @param {Object} pago - Datos del pago
 * @returns {Promise} Resultado del registro
 */
export const registrarPagoServicioExterno = async (pago) => {
    try {
        const response = await apiFetch('/PagoServicioExterno', {
            method: 'POST',
            body: {
                idServicioExterno: pago.idServicioExterno,
                montoUsd: pago.montoUsd,
                montoBs: pago.montoBs || 0,
                metodoPago: pago.metodoPago || 'Efectivo',
                referencia: pago.referencia || '',
                idUsuario: pago.idUsuario || 1,
                observaciones: pago.observaciones || ''
            }
        });

        // Si es un crédito con cuotas, actualizar el estado
        if (pago.idCuota) {
            try {
                await apiFetch(`/CreditoServicioExterno/cuota/${pago.idCuota}/pagar`, {
                    method: 'POST',
                    body: {
                        idCreditoServicioExterno: pago.idCuota,
                        montoPagoUsd: pago.montoUsd,
                        montoPagoBs: pago.montoBs || 0,
                        fechaPago: pago.fechaPago || new Date().toISOString(),
                        observaciones: pago.referencia || "Pago de cuota"
                    }
                });
            } catch (errCuotaSE) {
                console.warn('⚠️ Error registrando cuota de servicio externo:', errCuotaSE);
            }
        }

        return {
            success: true,
            data: response.data || response,
            message: 'Pago de servicio externo registrado exitosamente'
        };
    } catch (error) {
        console.error('❌ Error registrando pago de servicio externo:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Obtener pagos realizados a una compra específica
 * @param {number} idCompra - ID de la compra
 * @returns {Promise} Lista de pagos
 */
export const obtenerPagosCompra = async (idCompra) => {
    try {
        const response = await apiFetch(`/PagoCompra/compra/${idCompra}`);
        return {
            success: true,
            data: response.data || response,
            message: 'Pagos obtenidos exitosamente'
        };
    } catch (error) {
        console.error('❌ Error obteniendo pagos de compra:', error);
        return {
            success: false,
            error: error.message,
            data: []
        };
    }
};

/**
 * Obtener pagos realizados a un servicio externo específico
 * @param {number} idServicioExterno - ID del servicio externo
 * @returns {Promise} Lista de pagos
 */
export const obtenerPagosServicioExterno = async (idServicioExterno) => {
    try {
        const response = await apiFetch(`/PagoServicioExterno/servicio/${idServicioExterno}`);
        return {
            success: true,
            data: response.data || response,
            message: 'Pagos obtenidos exitosamente'
        };
    } catch (error) {
        console.error('❌ Error obteniendo pagos de servicio externo:', error);
        return {
            success: false,
            error: error.message,
            data: []
        };
    }
};

/**
 * Obtener créditos de servicios externos por cliente
 * @param {number} idCliente - ID del cliente
 * @returns {Promise} Lista de créditos
 */
export const obtenerCreditosServicioExternoPorCliente = async (idCliente) => {
    try {
        const response = await apiFetch(`/CreditoServicioExterno/cliente/${idCliente}`);
        return {
            success: true,
            data: response.data || response,
            message: 'Créditos obtenidos exitosamente'
        };
    } catch (error) {
        console.error('❌ Error obteniendo créditos de servicio externo:', error);
        return {
            success: false,
            error: error.message,
            data: []
        };
    }
};

/**
 * Crear crédito para un servicio externo
 * @param {Object} credito - Datos del crédito
 * @returns {Promise} Resultado del registro
 */
export const crearCreditoServicioExterno = async (credito) => {
    try {
        const response = await apiFetch('/CreditoServicioExterno', {
            method: 'POST',
            body: {
                idServicioExterno: credito.idServicioExterno,
                idCliente: credito.idCliente,
                montoTotal: credito.montoTotal,
                montoPagado: credito.montoPagado || 0,
                saldoPendiente: credito.saldoPendiente || credito.montoTotal,
                fechaVencimiento: credito.fechaVencimiento || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                estado: credito.estado || 'PENDIENTE',
                observaciones: credito.observaciones || '',
                tipoCredito: credito.tipoCredito || 'EMPRESA',
                tasaInteres: credito.tasaInteres || 0,
                numeroCuotas: credito.numeroCuotas || 1
            }
        });
        return {
            success: true,
            data: response.data || response,
            message: 'Crédito creado exitosamente'
        };
    } catch (error) {
        console.error('❌ Error creando crédito de servicio externo:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

// EXPORTACIÓN POR DEFECTO (para compatibilidad)
export default {
    obtenerResumenCreditos,
    registrarPagoCompra,
    registrarPagoReparacion,
    registrarPagoServicioExterno,
    obtenerPagosCompra,
    obtenerPagosServicioExterno,
    obtenerCreditosServicioExternoPorCliente,
    crearCreditoServicioExterno
};